'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Typography } from '@mui/material';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { useDonutChartStyles } from './useDonutChartStyles';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
ChartJS.register(ArcElement, Tooltip, Legend);
const DonutChart = ({ title, centerText, data, borderWidth = 0, ringThickness = '90%', height = 200, fillParent, centerTitle = true, onHiddenIndicesChange }) => {
    const styles = useDonutChartStyles();
    const { t } = useTranslation();
    const [hiddenIndices, setHiddenIndices] = useState([]);
    const handleLegendClick = (idx) => {
        const next = hiddenIndices.includes(idx)
            ? hiddenIndices.filter((i) => i !== idx)
            : [...hiddenIndices, idx];
        setHiddenIndices(next);
        onHiddenIndicesChange?.(next);
    };
    const visibleTotal = data.values.reduce((sum, v, idx) => (hiddenIndices.includes(idx) ? sum : sum + v), 0);
    const displayCenterText = hiddenIndices.length > 0 ? String(visibleTotal) : centerText;
    const chartData = useMemo(() => ({
        labels: data.labels,
        datasets: [
            {
                data: data.values.map((v, idx) => (hiddenIndices.includes(idx) ? 0 : v)),
                backgroundColor: data.colors,
                borderWidth: borderWidth,
                cutout: ringThickness,
                borderRadius: 5
            }
        ]
    }), [data, hiddenIndices, borderWidth, ringThickness]);
    const doughnutLabel = {
        id: 'doughnutLabel',
        afterDatasetsDraw(chart, args, plugins) {
            const meta = chart.getDatasetMeta(0);
            let centerX, centerY;
            if (!meta.data || !meta.data[0]) {
                centerX = chart.width / 2;
                centerY = chart.height / 2;
                chart.ctx.save();
                chart.ctx.font = '400 14px Suisse Intl, Arial, sans-serif';
                chart.ctx.fillStyle = '#161C24';
                chart.ctx.textAlign = 'center';
                chart.ctx.textBaseline = 'middle';
                chart.ctx.fillText(displayCenterText || t?.('events.noData') || 'No Data', centerX, centerY);
                chart.ctx.restore();
                return;
            }
            centerX = meta.data[0].x;
            centerY = meta.data[0].y;
            chart.ctx.save();
            chart.ctx.font = '400 24px PowerGrotesk';
            chart.ctx.fillStyle = '#161C24';
            chart.ctx.textAlign = 'center';
            chart.ctx.textBaseline = 'middle';
            chart.ctx.fillText(displayCenterText || '', centerX, centerY);
            chart.ctx.restore();
        }
    };
    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        // Hover ("active" element) transitions animate by default in Chart.js, separately from
        // the initial draw-in animation. Disabling just this keeps hover response instant without
        // affecting the load-in animation.
        transitions: {
            active: {
                animation: { duration: 0 }
            }
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                backgroundColor: '#454F5B',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                cornerRadius: 10,
                padding: 10,
                displayColors: false
            }
        }
    }), []);
    return (_jsxs(Box, { sx: styles.container, children: [title && (_jsx(Box, { mb: 2.6, display: 'flex', justifyContent: centerTitle ? 'center' : 'flex-start', alignItems: centerTitle ? 'center' : 'flex-start', children: _jsx(Typography, { variant: 'h6', sx: styles.title, children: title }) })), _jsxs(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: fillParent ? '100%' : (height ?? 200),
                    minHeight: 0
                }, children: [_jsx(Box, { sx: { flex: 1, minHeight: 0, position: 'relative' }, children: _jsx(Doughnut, { data: chartData, options: options, plugins: [doughnutLabel] }, displayCenterText + hiddenIndices.join(',')) }), data.labels.length > 0 && (_jsx(Box, { sx: {
                            width: '100%',
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            pb: 1,
                            mt: 1
                        }, children: _jsx(Box, { sx: {
                                display: 'flex',
                                gap: 2,
                                minWidth: 'max-content',
                                justifyContent: 'center',
                                px: 1
                            }, children: data.labels.map((label, idx) => (_jsxs(Box, { onClick: () => handleLegendClick(idx), sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: 0,
                                    cursor: 'pointer',
                                    opacity: hiddenIndices.includes(idx) ? 0.5 : 1,
                                    transition: 'opacity 0.2s',
                                    '&:hover': {
                                        opacity: hiddenIndices.includes(idx) ? 0.6 : 0.8
                                    }
                                }, children: [_jsx(Box, { sx: {
                                            width: 16,
                                            height: 16,
                                            backgroundColor: data.colors[idx],
                                            borderRadius: '50%',
                                            flexShrink: 0,
                                            border: hiddenIndices.includes(idx) ? '2px solid #ccc' : 'none'
                                        } }), _jsx(Typography, { sx: {
                                            whiteSpace: 'nowrap',
                                            fontSize: '10px',
                                            color: '#161C24',
                                            textDecoration: hiddenIndices.includes(idx) ? 'line-through' : 'none'
                                        }, children: label })] }, idx))) }) }))] })] }));
};
export default DonutChart;
